import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/app/hooks";
import { Save, Trash2, Search, Edit2, Plus, Info } from "lucide-react";
import {
  deleteMembershipSku,
  fetchMembershipSkus,
  selectMembershipSkuLoading,
  selectMembershipSkus,
  setCurrentSku,
  updateMembershipSku,
} from "@/modules/hub/features/MembershipSkuManagement/slice/membershipSkuSlice";
import type { MembershipSku } from "@/modules/hub/features/MembershipSkuManagement/schemas/membershipSkuSchema";
import { EditMembershipSkuDrawer } from "@/modules/hub/features/MembershipSkuManagement/components/EditMembershipSkuDrawer/EditMembershipSkuDrawer";
import { DeleteConfirmDialog } from "@/modules/hub/features/MembershipSkuManagement/components/DeleteConfirmDialog/DeleteConfirmDialog";
import { createMembershipSku } from "@/api/createMembershipSku/createMembershipSkuApi";

interface MembershipSkuSectionProps {
  companyUuid: string;
  companyId: number;
  currentTenant?: string;
}

export const MembershipSkuSection = ({
  currentTenant,
}: MembershipSkuSectionProps) => {
  const dispatch = useAppDispatch();
  const skus = useAppSelector(selectMembershipSkus);
  const loading = useAppSelector(selectMembershipSkuLoading);

  const [searchTerm, setSearchTerm] = useState("");
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [skuToDelete, setSkuToDelete] = useState<MembershipSku | null>(null);
  const [localSkus, setLocalSkus] = useState<MembershipSku[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Add new SKU form state
  const [newSkuPattern, setNewSkuPattern] = useState("");
  const [membershipType, setMembershipType] = useState<"annual" | "monthly">(
    "annual",
  );
  const [description, setDescription] = useState("");
  const [validationError, setValidationError] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    console.log(
      "[MembershipSkuSection] Fetching SKUs for tenant:",
      currentTenant,
    );
    dispatch(fetchMembershipSkus(currentTenant));
  }, [dispatch, currentTenant]);

  useEffect(() => {
    const skuArray = Array.isArray(skus) ? skus : [];
    setLocalSkus(skuArray);
    setHasChanges(false); // Reset changes when data is reloaded
    console.log("[MembershipSkuSection] SKUs from Redux:", skuArray);
    console.log("[MembershipSkuSection] Loading:", loading);
  }, [skus, loading]);

  const handleToggleStatus = (uuid: string) => {
    setLocalSkus((prev) =>
      prev.map((sku) =>
        sku.uuid === uuid ? { ...sku, isActive: !sku.isActive } : sku,
      ),
    );
    setHasChanges(true);
  };

  const handleEdit = (sku: MembershipSku) => {
    dispatch(setCurrentSku(sku));
    setIsEditOpen(true);
  };

  const handleDelete = (sku: MembershipSku) => {
    setSkuToDelete(sku);
  };

  const confirmDelete = async () => {
    if (skuToDelete) {
      await dispatch(deleteMembershipSku(skuToDelete.uuid));
      setSkuToDelete(null);
    }
  };

  const handleSaveAllChanges = async () => {
    setIsSaving(true);
    try {
      // Update each SKU that has been modified
      for (const sku of localSkus) {
        const originalSku = skus.find((s) => s.uuid === sku.uuid);
        if (originalSku && originalSku.isActive !== sku.isActive) {
          await dispatch(
            updateMembershipSku({
              uuid: sku.uuid,
              data: {
                skuPattern: sku.skuPattern,
                membershipDuration: sku.membershipDuration,
                description: sku.description,
                isActive: sku.isActive,
              },
            }),
          ).unwrap();
        }
      }
      setHasChanges(false);
      // Refresh the list
      dispatch(fetchMembershipSkus(currentTenant));
    } catch (error) {
      console.error("Failed to save changes:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddSku = async () => {
    setValidationError("");

    if (!newSkuPattern.trim()) {
      setValidationError("SKU code is required");
      return;
    }

    const skuArray = Array.isArray(skus) ? skus : [];
    if (
      skuArray.some(
        (s) => s.skuPattern?.toLowerCase() === newSkuPattern.toLowerCase(),
      )
    ) {
      setValidationError("This SKU already exists");
      return;
    }

    if (!currentTenant) {
      setValidationError("Tenant information is missing");
      return;
    }

    try {
      setIsCreating(true);
      await createMembershipSku({
        tenant: currentTenant,
        skuPattern: newSkuPattern,
        membershipDuration: membershipType,
        description: description || undefined,
      });

      // Refresh the list
      dispatch(fetchMembershipSkus(currentTenant));
      setNewSkuPattern("");
      setMembershipType("annual");
      setDescription("");
    } catch (error) {
      console.error("Failed to create membership SKU:", error);
      setValidationError("Failed to create SKU. Please try again.");
    } finally {
      setIsCreating(false);
    }
  };

  const filteredSkus = localSkus.filter(
    (sku) =>
      sku.skuPattern?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      sku.description?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const getValidityPeriod = (duration: string) => {
    return duration === "annual" ? "12 months" : "30 days";
  };

  const getMembershipTypeLabel = (duration: string) => {
    return duration === "annual" ? "Annual" : "Monthly";
  };

  const activeCount = localSkus.filter((sku) => sku.isActive).length;
  const totalCount = localSkus.length;

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Club Membership SKU Configuration
        </h2>
        <p className="text-gray-600 mb-4">
          Define the SKUs that identify club memberships in your QuickBooks
          invoices. These SKUs will be used to automatically detect and tag club
          members.
        </p>
        <div className="flex gap-6">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-sm text-gray-700">
              {activeCount} Active SKUs
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
            <span className="text-sm text-gray-700">
              {totalCount} Total SKUs
            </span>
          </div>
        </div>
      </div>

      {/* Add New Membership SKU Form */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-6">
          Add New Membership SKU
        </h3>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SKU Code <span className="text-red-500">*</span>
            </label>
            <input
              className="w-full px-4 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onChange={(e) => setNewSkuPattern(e.target.value)}
              placeholder="e.g., CLUB-ANNUAL"
              type="text"
              value={newSkuPattern}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Membership Type
            </label>
            <select
              className="w-full px-4 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              onChange={(e) =>
                setMembershipType(e.target.value as "annual" | "monthly")
              }
              value={membershipType}
            >
              <option value="annual">Annual (12 months)</option>
              <option value="monthly">Monthly (30 days)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <input
              className="w-full px-4 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              type="text"
              value={description}
            />
          </div>

          {validationError && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
              {validationError}
            </div>
          )}

          <div className="pt-2">
            <button
              className="px-4 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2 transition-colors"
              disabled={isCreating}
              onClick={handleAddSku}
            >
              <Plus className="w-4 h-4" />
              {isCreating ? "Adding..." : "Add SKU"}
            </button>
          </div>
        </div>
      </div>

      {/* Configured SKUs Table */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-900">Configured SKUs</h3>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  className="pl-9 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search SKUs..."
                  type="text"
                  value={searchTerm}
                />
              </div>
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium flex items-center gap-2 transition-colors"
                disabled={!hasChanges || isSaving}
                onClick={handleSaveAllChanges}
              >
                <Save className="w-4 h-4" />
                {isSaving ? "Saving..." : "Save All Changes"}
              </button>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-12 text-gray-500">Loading SKUs...</div>
        )}

        {/* Empty State */}
        {!loading && filteredSkus.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            {searchTerm
              ? "No SKUs found matching your search"
              : "No membership SKU patterns configured yet."}
          </div>
        )}

        {/* SKU Table */}
        {!loading && filteredSkus.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-700">
                    Status
                  </th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-700">
                    SKU Code
                  </th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-700">
                    Type
                  </th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-700">
                    Description
                  </th>
                  <th className="text-left py-3 px-6 text-sm font-medium text-gray-700">
                    Validity Period
                  </th>
                  <th className="text-right py-3 px-6 text-sm font-medium text-gray-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredSkus.map((sku) => (
                  <tr
                    className="hover:bg-gray-50 transition-colors"
                    key={sku.uuid}
                  >
                    <td className="py-4 px-6">
                      <button
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                          sku.isActive ? "bg-green-500" : "bg-gray-300"
                        }`}
                        onClick={() => handleToggleStatus(sku.uuid)}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            sku.isActive ? "translate-x-6" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </td>
                    <td className="py-4 px-6">
                      <code className="text-sm font-mono text-gray-900">
                        {sku.skuPattern}
                      </code>
                    </td>
                    <td className="py-4 px-6">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          sku.membershipDuration === "annual"
                            ? "bg-blue-100 text-blue-700"
                            : "bg-purple-100 text-purple-700"
                        }`}
                      >
                        {getMembershipTypeLabel(sku.membershipDuration)}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-600">
                      {sku.description || "-"}
                    </td>
                    <td className="py-4 px-6 text-sm text-gray-600">
                      {getValidityPeriod(sku.membershipDuration)}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex justify-end gap-2">
                        <button
                          className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                          onClick={() => handleEdit(sku)}
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                          onClick={() => handleDelete(sku)}
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Info Box */}
        <div className="m-6 bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-blue-900 mb-1">
              How it works:
            </p>
            <p className="text-sm text-blue-800">
              When invoices are synced from QuickBooks, the system will check
              each line item against these SKUs. If a match is found, the
              customer will be automatically tagged as a club member based on
              the membership type (annual = 12 months validity, monthly = 30
              days validity from invoice date).
            </p>
          </div>
        </div>
      </div>

      <EditMembershipSkuDrawer
        currentTenant={currentTenant}
        onClose={() => {
          setIsEditOpen(false);
          dispatch(fetchMembershipSkus(currentTenant));
        }}
        open={isEditOpen}
      />
      <DeleteConfirmDialog
        description={
          skuToDelete
            ? `Are you sure you want to delete the SKU pattern "${skuToDelete.skuPattern}"? This action cannot be undone.`
            : ""
        }
        onClose={() => setSkuToDelete(null)}
        onConfirm={confirmDelete}
        open={!!skuToDelete}
        title="Delete Membership SKU Pattern?"
      />
    </div>
  );
};
